<template>
  <div
    ref="wrapper"
    class="sidebar-wrapper"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <el-scrollbar wrap-class="scrollbar-wrapper">
      <el-menu
        :default-active="activeMenu"
        :collapse="effectiveCollapse"
        :background-color="variables.menuBg"
        :text-color="variables.menuText"
        :unique-opened="false"
        :active-text-color="variables.menuActiveText"
        :collapse-transition="false"
        mode="vertical"
      >
        <sidebar-item
          v-for="(route, routeIndex) in permission_routes"
          :key="getRouteKey(route, routeIndex)"
          :item="route"
          :base-path="route.path"
        />
      </el-menu>
    </el-scrollbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SidebarItem from './SidebarItem'
import variables from '@/styles/variables.scss'

const LEAVE_DELAY = 300 // 鼠标离开多久后收起 sidebar
const HOVER_EXPAND_CLASS = 'is-hover-expanded' // 加在 .sidebar-container 上的 class

export default {
  components: { SidebarItem },
  data() {
    return {
      isHoverExpanded: false,
      leaveTimer: null
    }
  },
  computed: {
    ...mapGetters(['permission_routes', 'sidebar']),
    routes() {
      return this.$router.options.routes
    },
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return path
    },
    variables() {
      return variables
    },
    // 用户主动折叠状态（来自 vuex）
    isCollapse() {
      return !this.sidebar.opened
    },
    // 实际传给 el-menu 的 collapse 值：
    // 折叠态下，鼠标 hover 时变成 false，菜单完整展开
    effectiveCollapse() {
      if (!this.isCollapse) return false
      return !this.isHoverExpanded
    }
  },
  watch: {
    // 监听 hover 状态变化，操作 .sidebar-container 的 class
    isHoverExpanded(val) {
      this.toggleContainerClass(val)
    },
    // 主动展开/折叠时，确保 hover class 状态正确
    isCollapse(val) {
      if (!val) {
        // 用户主动展开了：清除 hover 状态和 class
        this.isHoverExpanded = false
        this.toggleContainerClass(false)
      }
    }
  },
  beforeDestroy() {
    if (this.leaveTimer) clearTimeout(this.leaveTimer)
    // 组件销毁时清掉外部 class，避免残留
    this.toggleContainerClass(false)
  },
  methods: {
    getRouteKey(route, index) {
      const routeName = route.name || (route.meta && route.meta.title) || 'route'
      return `${route.path || '/'}::${routeName}::${index}`
    },

    /**
     * 找到外层 .sidebar-container 节点，加/移除 hover 展开的 class
     * 不用 :has() 选择器，兼容性更稳；不动 vuex，避免污染状态
     */
    toggleContainerClass(expanded) {
      const container = this.$refs.wrapper && this.$refs.wrapper.closest('.sidebar-container')
      if (!container) return
      if (expanded) {
        container.classList.add(HOVER_EXPAND_CLASS)
      } else {
        container.classList.remove(HOVER_EXPAND_CLASS)
      }
    },

    handleMouseEnter() {
      if (!this.isCollapse) return

      if (this.leaveTimer) {
        clearTimeout(this.leaveTimer)
        this.leaveTimer = null
      }
      this.isHoverExpanded = true
    },

    handleMouseLeave() {
      if (!this.isCollapse) return
      if (!this.isHoverExpanded) return

      this.leaveTimer = setTimeout(() => {
        this.isHoverExpanded = false
        this.leaveTimer = null
      }, LEAVE_DELAY)
    }
  }
}
</script>

<style lang="scss" scoped>
.sidebar-wrapper {
  height: 100%;
  width: 100%;
  background: #f0f6ff;

  ::v-deep .scrollbar-wrapper {
    overflow-x: hidden !important;
  }

  ::v-deep .el-scrollbar__bar.is-vertical {
    right: 0;
  }

  ::v-deep .el-scrollbar {
    height: 100%;
  }

  ::v-deep .el-menu {
    border: none;
  }
}
</style>
